<template>
  <b-container fluid class="d-flex flex-column py-3">
    <b-row class="mb-3">
      <b-col>
        <b-form-select
          v-model="item.readId"
          ref="selectReadId"
          :options="rules"
          @change="onChangeReadId"
        ></b-form-select>
      </b-col>
    </b-row>
    <b-table
      v-if="visibleList"
      hover
      no-border-collapse
      selectable
      select-mode="single"
      sticky-header
      thead-tr-class="small"
      tbody-tr-class="h5"
      ref="selectableTable"
      :items="items"
      :fields="fields"
      @row-selected="onRowSelected"
    >
      <!-- 予定/実績 -->
      <template #cell(orderResult)="data"
        >{{ data.item.orderCount }} / {{ data.item.resultCount }}</template
      >
      <!-- 照合実績へ -->
      <template #cell(toDetail)>
        <i class="bi bi-chevron-right"></i>
      </template>
    </b-table>
  </b-container>
</template>

<script>
import { CompareValueMixin } from '@/mixins/CompareValueMixin';
import { getCompareOrderListAPI } from '@/api';
import { convertItemFormat, createReadSelectItems, getCompareHeaderFieldsList } from '@/compare';
import { allowDup, compareMode, dataLayout, dataType, inputMode, viewSts } from '@/constants';

export default {
  name: 'CompareOrderRead',
  data() {
    return {
      rules: [], // 読取ルール配列
      compareNm: null, // 読取ルール名
      fields: [], // 項目
      items: [], // 値
      selectedInputMode: null, // 選択中の入力モード
      nowCompareNo: 1 // 現在の照合NO
    };
  },
  async mounted() {
    // 画面を初期化
    this.$emit('initView', {
      viewId: this.$options.name, // 画面ID
      useToolButtons: true, // ツールボタン使用
      defaultInputMode: inputMode.BARCODE, // デフォルトバーコード
      usableInputModeBits: inputMode.BARCODE + inputMode.OCR // バーコード＋OCR
    });

    // 読取ルールリスト作成
    const reads = this.$store.getters.reads.filter(
      (read) => read.dataType === dataType.COMPARE_RESULT
    );
    this.rules = createReadSelectItems(reads);

    // 前画面からのパラメータをセット
    const read = this.$store.getters.currentRead;
    if (read) {
      // 読取ルール名取得
      this.compareNm = read.readNm;

      // 読取ルールを選択状態にする
      await this.onChangeReadId(read.readId);
    }
  },
  computed: {
    visibleList: function () {
      return this.viewState > 0;
    }
  },
  methods: {
    /**
     * 入力モード変更処理(親コンポーネントより)
     * @param {string} 入力モード(1:手入力、2:OCR、4:バーコード)
     */
    changeInputMode: function (mode) {
      this.selectedInputMode = mode;
    },
    /**
     * 入力クリア(親コンポーネントより)
     */
    clearInput: async function () {
      // 読取ルールを再選択する
      await this.onChangeReadId(this.item.readId);
    },
    /**
     * スキャンデータ解析後事前処理
     * @param {number} viewState 画面状態
     * @param {object} headerValue ヘッダ値
     * @param {array} detailValues 明細値配列
     * @return {number} 画面状態、-1:解析エラー
     */
    analyzedBefore: async function (viewState, headerValue, detailValues) {
      // スキャンデータが解析できなかった場合はエラー
      if (Object.keys(headerValue).length === 0 && detailValues.length === 0) {
        this.$store.commit('setMessage', 'スキャンデータ解析エラー');
        return viewSts.ERROR;
      }

      // 明細スキャン中にヘッダ情報が割り込んだ場合別予定として継続する
      if (viewState === viewSts.ORDER_DETAIL && Object.keys(headerValue).length > 0) {
        return viewSts.READ_RULE; // 読取ルール選択後
      }

      return viewState;
    },
    /**
     * スキャンデータ解析後Step1
     * @param {object} headerValue ヘッダ値
     * @param {array} detailValues 明細値配列
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {number} dupType 重複区分(1:重複OK、2:重複NG)
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep1: async function (headerValue, detailValues, barcode, nextBarcode, dupType, item) {
      // 初期値セット
      item.compareOrderNo = 0; // 照合予定NO
      item.orderCount = 0; // 予定件数
      item.resultCount = 0; // 実績件数
      item.details.splice(0); // 明細

      this.items.splice(0, 0, item);

      // ヘッダ情報セット
      const compare = this.$store.getters.currentCompare;
      this.nowCompareNo = this.readOrderHeaderValue(barcode, compare, 1, headerValue, item);
      if (this.nowCompareNo === -1) {
        this.$store.commit('setMessage', '初回照合予定ヘッダ読取エラー');
        return viewSts.ERROR;
      }

      if (detailValues.length > 0) {
        // 明細情報セット
        for (let i = 0; i < detailValues.length; i++) {
          this.nowCompareNo = this.readOrderDetailValue(
            barcode,
            compare,
            1,
            i,
            detailValues[i],
            item
          );
          if (this.nowCompareNo === -1) {
            this.$store.commit('setMessage', '初回照合予定明細読取エラー');
            return viewSts.ERROR;
          }
        }

        if (nextBarcode) {
          this.$store.commit('setMessage', `照合予定(${nextBarcode.barcodeNm})を読取します`);
          return viewSts.ORDER_DETAIL; // 予定明細読取中
        } else {
          // 重複NGの場合は照合予定重複チェックを行う
          if (dupType === allowDup.DUP_NG) {
            const ret = await this.checkDuplicatedCompareOrder(compare, item, -1);
            if (!ret) {
              this.$store.commit('setMessage', '通信エラー(初回予定重複取得)');
              return viewSts.ERROR;
            }

            if (ret.compareOrderNo !== 0) {
              this.$store.commit('setMessage', '既に照合予定登録済みです');
              return viewSts.ERROR;
            }
          }

          for (let i = 0; i < detailValues.length; i++) {
            // 照合予定書き込み処理
            const ret = await this.writeCompareOrder(compare, item, i);
            if (!ret) {
              this.$store.commit('setMessage', '通信エラー(初回照合予定登録)');
              return viewSts.ERROR;
            }

            item.compareOrderNo = ret.compareOrderNo;
          }

          this.$set(item, 'orderCount', item.details.length);
          this.nowCompareNo = 1;

          this.$store.commit('setMessage', '次の照合予定を読取します');
          return viewSts.READ_RULE; // 読取ルール選択後
        }
      } else {
        if (nextBarcode) {
          this.$store.commit('setMessage', `照合予定(${nextBarcode.barcodeNm})を読取します`);
          return viewSts.ORDER_HEADER; // 予定ヘッダ読取中(継続)
        } else {
          this.nowCompareNo = 1;
          this.$store.commit('setMessage', '照合予定明細を読取します');
          return viewSts.ORDER_DETAIL; // 予定明細読取中
        }
      }
    },
    /**
     * スキャンデータ解析後Step2
     * @param {object} headerValue ヘッダ値
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep2: async function (headerValue, barcode, nextBarcode, item) {
      // ヘッダ読取
      const compare = this.$store.getters.currentCompare;
      this.nowCompareNo = this.readOrderHeaderValue(
        barcode,
        compare,
        this.nowCompareNo,
        headerValue,
        item
      );
      if (this.nowCompareNo === -1) {
        this.$store.commit('setMessage', '照合予定ヘッダ読取エラー');
        return viewSts.ERROR;
      }

      if (nextBarcode) {
        this.$store.commit('setMessage', `照合予定(${nextBarcode.barcodeNm})を読取します`);
        return viewSts.ORDER_HEADER; // 予定ヘッダ読取中(継続)
      } else {
        this.$store.commit('setMessage', '照合予定明細を読取します');
        return viewSts.ORDER_DETAIL; // 予定明細読取中
      }
    },
    /**
     * スキャンデータ解析後Step3
     * @param {array} detailValues 明細値配列
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {number} dupType 重複区分(1:重複OK、2:重複NG)
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep3: async function (detailValues, barcode, nextBarcode, dupType, item) {
      const compare = this.$store.getters.currentCompare;
      this.nowCompareNo = this.readOrderDetailValue(
        barcode,
        compare,
        this.nowCompareNo,
        0,
        detailValues[0],
        item
      );
      if (this.nowCompareNo === -1) {
        this.$store.commit('setMessage', '照合予定明細読取エラー');
        return viewSts.ERROR;
      }

      if (nextBarcode) {
        // 予定明細読取中(継続)
        this.$store.commit('setMessage', `照合予定(${nextBarcode.barcodeNm})を読取します`);
        return viewSts.ORDER_DETAIL; // 予定明細読取中(継続)
      } else {
        // 重複NGの場合は照合予定重複チェックを行う
        if (dupType === allowDup.DUP_NG) {
          const ret = await this.checkDuplicatedCompareOrder(compare, item, 0);
          if (!ret) {
            this.$store.commit('setMessage', '通信エラー(予定重複取得)');
            return viewSts.ERROR;
          }

          if (ret.compareOrderNo !== 0) {
            this.$store.commit('setMessage', '既に照合予定登録済みです');
            return viewSts.ERROR;
          }
        }

        // 照合予定書き込み処理
        const ret = await this.writeCompareOrder(compare, item, 0);
        if (!ret) {
          this.$store.commit('setMessage', '通信エラー(予定登録)');
          return viewSts.ERROR;
        }

        item.compareOrderNo = ret.compareOrderNo;

        const order = this.items.find(function (x) {
          return x.compareOrderNo === ret.compareOrderNo;
        });
        if (order) {
          this.$set(item, 'orderCount', item.orderCount + 1);
        } else {
          item.details.push(order);
        }

        const headerField = compare.details.find(function (x) {
          return x.dataLayout === dataLayout.HEADER;
        });
        if (!headerField) {
          // 照合ヘッダ有の場合
          this.nowCompareNo = 1;
          this.$store.commit('setMessage', '次の照合予定明細を読取します');
          return viewSts.ORDER_DETAIL; // 照合予定明細読取中
        } else {
          // 照合ヘッダ無の場合
          this.$store.commit('setMessage', '照合予定を読取/選択します');
          return viewSts.READ_RULE; // 読取ルール選択後
        }
      }
    },
    /**
     * 読取ルール変更時
     * @param {string} event イベント引数(読取ID)
     */
    onChangeReadId: async function (event) {
      this.$store.commit('setMessage', null);

      // 読取IDを保持
      this.$store.commit('setReadId', event);

      // 画面情報の属性をセット
      const read = this.$store.getters.currentRead;
      if (!read) {
        this.item.readId = null;
        this.viewState = viewSts.INIT; // 初期表示
        return;
      }

      this.item.readId = read.readId; // 読取ID
      this.item.dataType = read.dataType; // データ区分
      this.item.orderCount = 0; // 予定件数
      this.item.resultCount = 0; // 実績件数
      this.item.details.splice(0); // 明細

      // 一覧表示項目選択
      const compare = this.$store.getters.currentCompare;
      if (compare) {
        const fields = getCompareHeaderFieldsList(compare);
        fields.push({ key: 'orderResult', label: '予/実' });
        this.fields = fields.map(function (x) {
          return { key: x.key, label: x.label, thClass: 'p-1', tdClass: 'px-1' };
        });
        this.fields.push({
          key: 'toDetail',
          label: '',
          thClass: 'p-1',
          tdClass: 'text-right px-0'
        });

        // 照合実績情報リスト検索
        await this.getCompareOrderList(read.readId, compare);
      } else {
        this.fields.splice(0);
        this.items.splice(0);
      }

      this.viewState = 1; // 読取ルール選択後
      this.$store.commit('setMessage', '照合予定を読取/選択します');

      // 読取ルールのフォーカスを外す
      this.$refs.selectReadId?.blur();
    },
    /**
     * 照合予定行選択時
     * @param {any} selectedItems 選択項目(照合予定)
     */
    onRowSelected: function (selectedItems) {
      // 次画面へのパラメータセット
      this.$store.commit('incrementHistoryCount');

      // 照合実績画面へ
      const url = `/CompareResult/${selectedItems[0].compareOrderNo}`;
      this.$router.push({ path: url });
    },
    /**
     * 照合予定情報リスト取得
     * @param {number} readId 読取ID
     * @param {any} compare 照合情報
     */
    getCompareOrderList: async function (readId, compare) {
      // 照合予定情報リストAPI
      const ret = await getCompareOrderListAPI(readId, null);
      if (!ret) {
        this.items = [];
        this.viewState = viewSts.READ_RULE; // 読取ルール選択後
        this.$store.commit('setMessage', '通信エラー(照合予定リスト取得)');
        return;
      }

      this.items = ret.data;
      for (let item of this.items) {
        convertItemFormat(compare, item);
      }

      this.viewState = viewSts.READ_RULE; // 読取ルール選択後
      this.$store.commit('setMessage', '照合予定を読取/選択します');
    }
  },
  mixins: [CompareValueMixin]
};
</script>

<style>
div.container-fluid {
  height: 100%;
}
.b-table-sticky-header {
  max-height: none !important;
  -webkit-flex: 1 1 0;
  -ms-flex: 1 1 0;
  flex: 1 1 0;
}
</style>
